import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  WorkItem,
  WorkItemComment,
  ActivityEntry,
  Project,
  WorkPriority,
  WorkflowDefinition,
  AvailableTransitionEntry,
} from '@captain-tasko/shared';
import { WORK_PRIORITIES } from '@captain-tasko/shared';
import { apiFetch, apiList, apiSingle, apiUpload, ApiError } from '../lib/api.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { useRealtime } from '../lib/realtime.js';
import { toast } from '../components/Toast.js';
import { copyToClipboard } from '../lib/clipboard.js';
import { LinkedDocumentsPanel } from '../components/LinkedDocuments.js';
import { AttachmentsPanel } from '../components/AttachmentsPanel.js';
import { NotesPanel } from '../components/NotesPanel.js';
import { MarkdownView } from '../components/MarkdownView.js';
import { GroupMultiSelect, UserChips, UserMultiSelect, UserSelect } from '../components/UserPicker.js';
import { VisibilityToggle } from '../components/VisibilityToggle.js';
import { useSession } from '../hooks/useSession.js';
import { useDocumentGrouping } from '../hooks/useDocumentGrouping.js';
import { batchFields, batchIdForAction } from '../lib/ingest-batch.js';
import { daysPastDue, formatDaysPastDueLong, formatWorkItemKey } from '../lib/format.js';

// ctsk-115: lightweight shape used by parent/children surfacing on the
// detail page. Server returns these from GET /workitems/:id alongside
// the existing envelope fields.
interface WorkItemRef {
  id: string;
  key: string;
  title: string;
  status: string;
}

interface DetailEnvelope extends WorkItem {
  comments: WorkItemComment[];
  // CTSK-226 — {to, allowed, reason?, message?, capability?}[]; was
  // string[] pre-CTSK-226.
  availableTransitions: AvailableTransitionEntry[];
  // ctsk-115 structural relationship surface
  parent?: WorkItemRef | null;
  children?: WorkItemRef[];
}

export function WorkItemDetail({ id, onNavigate }: { id: string; onNavigate: (p: string) => void }) {
  const qc = useQueryClient();
  // CTSK-500 — same gate as the Bulk Edit button (env flag + org setting).
  const { enabled: groupingEnabled } = useDocumentGrouping();
  const { session } = useSession();
  const detail = useQuery({
    queryKey: ['workitem', id],
    queryFn: () => apiSingle<DetailEnvelope>(`/workitems/${id}`),
  });
  // 0.1.51 — projects list so the page header key can render in the
  // owning project's color instead of --text-muted.
  const projectsQ = useQuery({
    queryKey: ['projects'],
    queryFn: () => apiList<Project>('/projects'),
  });

  // Load the workflow so we can show the full state sequence in the picker.
  const workflowsQ = useQuery({
    queryKey: ['workflows'],
    queryFn: () => apiList<WorkflowDefinition>('/workflows'),
    staleTime: 60_000,
  });
  const workflow = detail.data
    ? workflowsQ.data?.data.find((w) => w.id === detail.data!.workflowId)
    : undefined;

  useRealtime({
    types: ['work.updated', 'work.status_changed', 'work.deleted'],
    onEvent: (e) => {
      const payload = e.payload as { id?: string };
      if (payload?.id === id) qc.invalidateQueries({ queryKey: ['workitem', id] });
    },
  });

  const patch = useMutation({
    mutationFn: (body: Partial<WorkItem>) =>
      apiSingle<WorkItem>(`/workitems/${id}`, {
        method: 'PATCH',
        body,
        ifMatch: detail.data?.etag,
      }),
    onSuccess: () => { toast.success('Saved'); qc.invalidateQueries({ queryKey: ['workitem', id] }); qc.invalidateQueries({ queryKey: ['workitems'] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const transition = useMutation({
    mutationFn: (to: string) => apiSingle(`/workitems/${id}/transition`, { method: 'POST', body: { to } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['workitem', id] }); qc.invalidateQueries({ queryKey: ['workitems'] }); },
    onError: (e: Error) => {
      if (e instanceof ApiError && Array.isArray(e.details)) {
        const reasons = (e.details as { message?: string; guard?: string }[]).map((d) => d.message ?? d.guard).join('; ');
        toast.error(reasons || e.message);
      } else toast.error(e.message);
    },
  });

  const del = useMutation({
    mutationFn: () => apiFetch<void>(`/workitems/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); onNavigate('/workitems'); },
  });

  const comment = useMutation({
    mutationFn: (body: string) =>
      apiSingle<WorkItemComment>(`/workitems/${id}/comments`, { method: 'POST', body: { body } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workitem', id] }),
    onError: (e: Error) => toast.error(e.message),
  });

  // CTSK-372 — author-scoped comment edit. Keeps the edit textarea open
  // on error (per the brief: "surface inline + keep edit mode open")
  // instead of the toast-and-collapse pattern the top-level `comment`
  // mutation above uses — see editCommentError state below.
  const editComment = useMutation({
    mutationFn: ({ commentId, body }: { commentId: string; body: string }) =>
      apiSingle<WorkItemComment>(`/workitems/${id}/comments/${commentId}`, { method: 'PATCH', body: { body } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workitem', id] });
      setEditingCommentId(null);
      setEditCommentError(null);
    },
    onError: (e: Error) => setEditCommentError(e.message),
  });

  // #112 — Toggle the caller's watch state for this work item. Idempotent
  // on the server (POST on already-watching is a no-op; DELETE on
  // not-watching same). Invalidates both the detail key and the list key
  // so the watching quick filter on the list view stays in sync.
  const watch = useMutation({
    mutationFn: (next: boolean) =>
      apiSingle<{ workItemId: string; isWatchedByMe: boolean; watcherCount: number }>(
        `/workitems/${id}/watch`,
        { method: next ? 'POST' : 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workitem', id] });
      qc.invalidateQueries({ queryKey: ['workitems'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [editingBody, setEditingBody] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [commentBody, setCommentBody] = useState('');
  const [labelInput, setLabelInput] = useState('');

  // CTSK-372 — inline comment editor state. Only one comment editable at
  // a time (editingCommentId), mirroring the single editingBody flag the
  // description editor above uses.
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentBody, setEditCommentBody] = useState('');
  const [editCommentError, setEditCommentError] = useState<string | null>(null);

  // #113 — drop-files-into-note state. Drag-over highlights the notes
  // section; `noteAttachments` tracks per-file upload status so the user
  // sees progress and any errors inline.
  const [noteDragOver, setNoteDragOver] = useState(false);
  type AttachStatus = 'uploading' | 'attaching' | 'done' | 'error';
  interface AttachState {
    id: number;       // local ordinal, just for React keys
    filename: string;
    status: AttachStatus;
    error?: string;
    documentId?: string;
    /** human-readable container slug the doc landed in, if any */
    routedTo?: string;
  }
  const [noteAttachments, setNoteAttachments] = useState<AttachState[]>([]);
  const attachIdRef = useRef(0);

  /**
   * Upload each dropped/picked file, attach it to this work item, then
   * append a Markdown link to the comment textarea. Sequential (per the
   * #113 design notes) so per-file status is easy to read and we don't
   * flood the upload endpoint. Each step updates noteAttachments so the
   * UI can render progress.
   */
  async function uploadAndAttachFiles(files: FileList | File[]) {
    const list = Array.from(files);
    if (list.length === 0) return;
    // CTSK-500 — one batch id for this whole multi-file action (null for a
    // single file or when grouping is off). Minted once, before the loop.
    const batchId = batchIdForAction(list.length, groupingEnabled);
    for (const file of list) {
      const localId = ++attachIdRef.current;
      setNoteAttachments((prev) => [...prev, { id: localId, filename: file.name, status: 'uploading' }]);
      try {
        // 1) Upload.
        const uploadRes = await apiUpload<{ document: { id: string; title: string } }>(
          '/documents/upload',
          file,
          batchFields(batchId),
        );
        const documentId = uploadRes.document.id;
        setNoteAttachments((prev) =>
          prev.map((a) => (a.id === localId ? { ...a, status: 'attaching', documentId } : a)),
        );

        // 2) Attach to this work item (link + tags + container).
        const attachRes = await apiSingle<{
          workItemId: string;
          documentId: string;
          tagsApplied: string[];
          containersAssigned: string[];
        }>(`/workitems/${id}/documents/attach`, { method: 'POST', body: { documentId } });

        setNoteAttachments((prev) =>
          prev.map((a) =>
            a.id === localId
              ? { ...a, status: 'done', routedTo: attachRes.containersAssigned[0] }
              : a,
          ),
        );

        // 3) Append a Markdown link to the comment draft. Keeps the
        // user's note still pending — they click "Add note" to commit.
        setCommentBody((prev) => {
          const link = `[${file.name}](/documents/${documentId})`;
          if (!prev.trim()) return link;
          return prev.endsWith('\n') ? `${prev}${link}\n` : `${prev}\n${link}\n`;
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        setNoteAttachments((prev) =>
          prev.map((a) => (a.id === localId ? { ...a, status: 'error', error: message } : a)),
        );
        toast.error(`${file.name}: ${message}`);
      }
    }
    // Refresh both the work item (related docs panel) and the documents
    // list so the new attachments appear elsewhere too.
    qc.invalidateQueries({ queryKey: ['workitem', id] });
    qc.invalidateQueries({ queryKey: ['documents'] });
  }

  function dismissAttachment(localId: number) {
    setNoteAttachments((prev) => prev.filter((a) => a.id !== localId));
  }

  useEffect(() => {
    if (detail.data) {
      setEditTitle(detail.data.title);
      setEditDesc(detail.data.description ?? '');
    }
  }, [detail.data, editingBody]);

  // 0.1.52 — CTSK-167 Issue B target: honor ?tab=documents by scrolling
  // to the Attachments panel once the detail is loaded. The Docs chip in
  // WorkItemsList navigates here with `?tab=documents`. Uses a smooth
  // scroll so it's obvious to the user that the click did SOMETHING —
  // the visual bump surfaces that the workitem opened AND landed at the
  // docs section, not just top-of-page like a plain row click.
  useEffect(() => {
    if (!detail.data) return;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') !== 'documents') return;
    // Defer one frame so React has committed the AttachmentsPanel DOM.
    requestAnimationFrame(() => {
      const el = document.getElementById('attachments');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }, [detail.data]);

  if (detail.isLoading) return <SkeletonRows rows={10} />;
  if (detail.isError || !detail.data) return <div>Work item not found.</div>;

  const w = detail.data;

  return (
    <>
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {/* Display key: externalId leads when present, key as
                tooltip; falls back to key-only for UI-created items.
                See FEATURE_REQUESTS.md "Align display key with
                external_id." */}
            {(() => {
              const display = formatWorkItemKey(w);
              const projectColor = projectsQ.data?.data.find((p) => p.id === w.projectId)?.color;
              return (
                <code
                  // 0.1.51 — key color derived from the work item's
                  // project.color. Falls back to --text-muted when the
                  // project has no color set.
                  style={{
                    fontFamily: 'var(--font-mono)',
                    color: projectColor ?? 'var(--text-muted)',
                    fontWeight: projectColor ? 600 : undefined,
                  }}
                  title={display.secondary ?? undefined}
                >
                  {display.primary}
                </code>
              );
            })()}
            <h1 className="page-title" style={{ margin: 0 }}>{w.title}</h1>
            {/* Phase 4 visibility toggle. Owner/operator can flip;
                others see a read-only chip. */}
            <VisibilityToggle
              resourcePath="/workitems"
              resourceId={w.id}
              current={w.visibility ?? 'private'}
              resourceLabel="work item"
              canShare={session?.capabilities?.includes('workitems:share') ?? false}
              invalidateKeys={[['workitems'], ['workitem', w.id]]}
            />
          </div>
          <div className="page-subtitle">
            <StatusBadge status={w.status} variant="document" />
            <span style={{ marginLeft: 8, textTransform: 'capitalize' }}>priority: {w.priority}</span>
            {w.dueDate && (
              <span style={{ marginLeft: 12 }}>
                due {new Date(w.dueDate).toLocaleDateString()}
              </span>
            )}
            {(() => {
              // Days-past-due chip (#85). Renders only when API has
              // flagged isPastDue; uses long-form text since detail
              // headers have the room.
              const days = daysPastDue(w.isPastDue, w.dueDate);
              if (days <= 0) return null;
              return (
                <span
                  title={`Due ${new Date(w.dueDate!).toLocaleString()}`}
                  style={{
                    marginLeft: 12,
                    padding: '2px 8px',
                    borderRadius: 4,
                    background: 'color-mix(in srgb, var(--danger-primary, #B3261E) 10%, var(--surface-raised))',
                    border: '1px solid var(--danger-primary, #B3261E)',
                    color: 'var(--danger-primary, #B3261E)',
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  ↳ {formatDaysPastDueLong(days)}
                </span>
              );
            })()}
            {/* ctsk-115: parent breadcrumb chip. Renders only when this
                item has a parent. Clickable; navigates to the parent's
                detail page. The chevron `↑` reads as "go up the tree." */}
            {w.parent && (
              <button
                type="button"
                onClick={() => onNavigate(`/workitems/${w.parent!.id}`)}
                title={`Open parent: ${w.parent.title}`}
                style={{
                  marginLeft: 12,
                  padding: '2px 8px',
                  borderRadius: 4,
                  background: 'var(--surface-raised)',
                  border: '1px solid var(--border-subtle)',
                  color: 'var(--text-primary)',
                  fontSize: 11,
                  fontWeight: 500,
                  fontFamily: 'var(--font-mono)',
                  cursor: 'pointer',
                }}
              >
                ↑ Parent: {w.parent.key}
              </button>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* Watch toggle (#112). Star fills when watching; total watcher
              count surfaces beside the icon when ≥1 so multi-team
              ownership is visible at a glance. */}
          <button
            type="button"
            className="btn"
            onClick={() => watch.mutate(!w.isWatchedByMe)}
            disabled={watch.isPending}
            title={w.isWatchedByMe ? 'Stop watching this item' : 'Watch this item — appears in your Watching quick filter'}
            aria-pressed={w.isWatchedByMe ?? false}
            style={{
              background: w.isWatchedByMe ? 'color-mix(in srgb, var(--accent-primary) 12%, var(--surface-raised))' : undefined,
              borderColor: w.isWatchedByMe ? 'var(--accent-primary)' : undefined,
              color: w.isWatchedByMe ? 'var(--accent-primary)' : undefined,
              fontWeight: w.isWatchedByMe ? 600 : undefined,
            }}
          >
            {w.isWatchedByMe ? '★ Watching' : '☆ Watch'}
            {typeof w.watcherCount === 'number' && w.watcherCount > 0 && (
              <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>
                {w.watcherCount}
              </span>
            )}
          </button>
          {/* CTSK-235 — Copy short URL. Uses /wi/<key> shape so the
              clipboard payload is human-shareable (`…/wi/CTSK-161` reads
              far cleaner than the ULID form). Requires w.key to be set;
              falls back to ULID if the workitem somehow lacks a key. */}
          {w.key && (
            <button
              type="button"
              className="btn"
              title={`Copy short URL: /wi/${w.key}`}
              onClick={async () => {
                const url = `${window.location.origin}/wi/${w.key}`;
                const ok = await copyToClipboard(url);
                if (ok) toast.success('Short URL copied');
                else toast.error('Could not copy — your browser blocked clipboard access');
              }}
            >
              🔗 Copy link
            </button>
          )}
          {!editingBody && <button className="btn btn--primary" onClick={() => setEditingBody(true)}>✎ Edit</button>}
          <button className="btn btn--danger" onClick={() => { if (confirm(`Delete ${formatWorkItemKey(w).primary}?`)) del.mutate(); }}>Delete</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 24 }}>
        <div>
          <section style={{ background: 'var(--surface-raised)', padding: 20, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', marginBottom: 16 }}>
            <h3 style={{ marginTop: 0 }}>Description</h3>
            {editingBody ? (
              <>
                <input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  style={{ width: '100%', padding: 6, marginBottom: 8, border: '1px solid var(--border-strong)', borderRadius: 4 }}
                />
                <textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  rows={10}
                  style={{ width: '100%', padding: 8, fontFamily: 'inherit', border: '1px solid var(--border-strong)', borderRadius: 4 }}
                />
                <div style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn" onClick={() => setEditingBody(false)}>Cancel</button>
                  <button
                    className="btn btn--primary"
                    onClick={() => {
                      patch.mutate({ title: editTitle, description: editDesc });
                      setEditingBody(false);
                    }}
                  >
                    Save
                  </button>
                </div>
              </>
            ) : (
              w.description ? (
                <MarkdownView content={w.description} hideFrontmatterBanner constrainWidth={false} />
              ) : <span style={{ color: 'var(--text-muted)' }}>No description yet — click ✎ Edit to add one.</span>
            )}
          </section>

          {/* ctsk-115: Children section. Renders only when this item has
              children; lists each as a clickable row with key + status
              badge + title. Mirrors the pattern of the v0.1.24 crawl-tree
              widget on DocumentDetail.tsx so the visual language stays
              consistent for "structural list of related items". */}
          {w.children && w.children.length > 0 && (
            <section
              style={{
                background: 'var(--surface-raised)',
                padding: 20,
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-subtle)',
                marginBottom: 16,
              }}
            >
              <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                Children
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: 'var(--text-muted)',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {w.children.length}
                </span>
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {w.children.map((c) => (
                  <li
                    key={c.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '6px 0',
                      borderBottom: '1px solid var(--border-subtle)',
                    }}
                  >
                    <code
                      style={{
                        fontFamily: 'var(--font-mono)',
                        color: 'var(--text-muted)',
                        fontSize: 12,
                        minWidth: 80,
                      }}
                    >
                      {c.key}
                    </code>
                    <StatusBadge status={c.status} variant="document" />
                    <button
                      type="button"
                      onClick={() => onNavigate(`/workitems/${c.id}`)}
                      title={`Open ${c.key}`}
                      style={{
                        flex: 1,
                        textAlign: 'left',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: 'var(--accent-primary, #1976d2)',
                        cursor: 'pointer',
                        fontSize: 14,
                        textDecoration: 'underline',
                      }}
                    >
                      {c.title}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section
            // #113 — drop zone for the notes / comments area. Files
            // dropped here get uploaded as documents, linked to this
            // work item (context='attachment'), tagged + routed to the
            // Projects container if one exists, and the markdown link
            // is appended to the comment draft below.
            onDragEnter={(e) => { e.preventDefault(); setNoteDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); setNoteDragOver(false); }}
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => {
              e.preventDefault();
              setNoteDragOver(false);
              const files = e.dataTransfer.files;
              if (files && files.length > 0) uploadAndAttachFiles(files);
            }}
            style={{
              background: 'var(--surface-raised)',
              padding: 20,
              borderRadius: 'var(--radius-md)',
              border: `1px ${noteDragOver ? 'dashed' : 'solid'} ${noteDragOver ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
              transition: 'border-color 100ms',
            }}
          >
            <h3 style={{ marginTop: 0 }}>Notes / Comments</h3>
            {w.comments.length === 0 && <p style={{ color: 'var(--text-muted)' }}>No comments yet.</p>}
            {w.comments.map((c) => {
              // CTSK-372 — author-scoped edit. Own comment OR holding the
              // admin override cap; comments:edit_any is the only field
              // read here (server is still the source of truth — this
              // just decides whether to render the chip at all).
              const canEdit = session != null
                && (c.authorId === session.user.id || session.capabilities.includes('comments:edit_any'));
              const isEditingThis = editingCommentId === c.id;
              return (
              <div key={c.id} style={{ borderBottom: '1px solid var(--border-subtle)', padding: '12px 0' }}>
                {/* 0.1.52 — CTSK-168: server-hydrated authorDisplayName +
                    session sourceTag replace the bare ULID. Renders as
                    "Display Name  •  cowork-coo-mini-me" with the ULID
                    surfaced only in the tooltip. Fallback to shortened
                    ULID (01KWCZ…N6V1B2C) when hydration missed. */}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)', alignItems: 'baseline' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
                    <ActorIdentity
                      displayName={c.authorDisplayName ?? null}
                      email={c.authorEmail ?? null}
                      id={c.authorId}
                    />
                    {c.source && <SourceTagChip tag={c.source} />}
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
                    {c.editedAt && (
                      <span title={new Date(c.editedAt).toISOString()}>
                        (edited {new Date(c.editedAt).toISOString().slice(0, 16).replace('T', ' ')} UTC)
                      </span>
                    )}
                    <span>{new Date(c.createdAt).toLocaleString()}</span>
                    {canEdit && !isEditingThis && (
                      <button
                        type="button"
                        className="btn btn--ghost"
                        style={{ padding: '0 6px', fontSize: 12 }}
                        aria-label="Edit comment"
                        onClick={() => {
                          setEditingCommentId(c.id);
                          setEditCommentBody(c.body);
                          setEditCommentError(null);
                        }}
                      >
                        ✎ Edit
                      </button>
                    )}
                  </span>
                </div>
                <div style={{ marginTop: 4 }}>
                  {isEditingThis ? (
                    <>
                      <textarea
                        value={editCommentBody}
                        onChange={(e) => setEditCommentBody(e.target.value)}
                        rows={3}
                        aria-label="Edit comment body"
                        style={{ width: '100%', padding: 8, fontFamily: 'inherit', border: '1px solid var(--border-strong)', borderRadius: 4 }}
                      />
                      {editCommentError && (
                        <p style={{ color: 'var(--danger-primary, #B3261E)', fontSize: 12, margin: '4px 0 0' }}>
                          {editCommentError}
                        </p>
                      )}
                      <div style={{ marginTop: 6, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => {
                            setEditingCommentId(null);
                            setEditCommentError(null);
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn--primary"
                          disabled={!editCommentBody.trim() || editComment.isPending}
                          onClick={() => editComment.mutate({ commentId: c.id, body: editCommentBody })}
                        >
                          Save
                        </button>
                      </div>
                    </>
                  ) : (
                    <MarkdownView content={c.body} hideFrontmatterBanner constrainWidth={false} />
                  )}
                </div>
              </div>
              );
            })}
            {/* #113 — inline attachment status. Renders only while there
                are uploads in flight or recent failures. Each row gets
                a dismiss × button. */}
            {noteAttachments.length > 0 && (
              <ul
                style={{
                  listStyle: 'none',
                  margin: '12px 0 0',
                  padding: '8px 10px',
                  background: 'var(--surface-canvas)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 6,
                  fontSize: 12,
                }}
              >
                {noteAttachments.map((a) => (
                  <li
                    key={a.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}
                    title={a.error ?? `${a.status}${a.routedTo ? ` → ${a.routedTo}` : ''}`}
                  >
                    <span
                      style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background:
                          a.status === 'done' ? 'var(--success-primary, #3C8E7A)' :
                          a.status === 'error' ? 'var(--danger-primary, #B3261E)' :
                          'var(--accent-primary)',
                        flex: '0 0 8px',
                      }}
                    />
                    <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {a.filename}
                    </span>
                    <span style={{ color: 'var(--text-muted)' }}>
                      {a.status === 'uploading' && 'uploading…'}
                      {a.status === 'attaching' && 'attaching…'}
                      {a.status === 'done' && (a.routedTo ? `attached → ${a.routedTo}` : 'attached')}
                      {a.status === 'error' && (a.error ?? 'failed')}
                    </span>
                    <button
                      type="button"
                      onClick={() => dismissAttachment(a.id)}
                      className="btn btn--ghost"
                      style={{ padding: '0 6px', fontSize: 12 }}
                      aria-label={`Dismiss ${a.filename}`}
                    >×</button>
                  </li>
                ))}
              </ul>
            )}
            <div style={{ marginTop: 12 }}>
              <textarea
                value={commentBody}
                onChange={(e) => setCommentBody(e.target.value)}
                placeholder="Add a note or comment… (Markdown supported · drop files here to attach)"
                rows={3}
                style={{ width: '100%', padding: 8, fontFamily: 'inherit', border: '1px solid var(--border-strong)', borderRadius: 4 }}
              />
              <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                {/* Click-to-pick fallback for users who can't drag (and
                    keyboard accessibility). Same handler as the drop
                    zone — files flow through the same upload + attach
                    pipeline. */}
                <label className="btn btn--ghost" style={{ padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
                  <input
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files && files.length > 0) uploadAndAttachFiles(files);
                      e.target.value = '';
                    }}
                  />
                  📎 Attach files
                </label>
                <button
                  className="btn btn--primary"
                  disabled={!commentBody.trim() || comment.isPending}
                  onClick={() => { comment.mutate(commentBody); setCommentBody(''); }}
                >
                  Add note
                </button>
              </div>
            </div>
          </section>
        </div>

        <aside>
          {/* State picker — full sequence with current highlighted in place */}
          <div style={{ background: 'var(--surface-raised)', padding: 16, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', marginBottom: 16 }}>
            <h4 style={{ marginTop: 0 }}>State</h4>
            <StatePicker
              workflow={workflow}
              currentStatus={w.status}
              availableTransitions={w.availableTransitions}
              onTransition={(to) => transition.mutate(to)}
              pending={transition.isPending}
            />
          </div>

          {/* Priority */}
          <div style={{ background: 'var(--surface-raised)', padding: 16, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', marginBottom: 16 }}>
            <h4 style={{ marginTop: 0 }}>Priority</h4>
            <select
              value={w.priority}
              onChange={(e) => patch.mutate({ priority: e.target.value as WorkPriority })}
              style={{ width: '100%', padding: 6 }}
            >
              {WORK_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          {/* Due date */}
          <div style={{ background: 'var(--surface-raised)', padding: 16, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', marginBottom: 16 }}>
            <h4 style={{ marginTop: 0 }}>Due date</h4>
            <input
              type="date"
              value={w.dueDate ? w.dueDate.slice(0, 10) : ''}
              onChange={(e) => {
                const next = e.target.value ? new Date(e.target.value).toISOString() : null;
                patch.mutate({ dueDate: next });
              }}
              style={{ width: '100%', padding: 6 }}
            />
            {w.dueDate && (
              <button
                className="btn btn--ghost"
                style={{ marginTop: 6, fontSize: 12 }}
                onClick={() => patch.mutate({ dueDate: null })}
              >
                Clear
              </button>
            )}
          </div>

          {/* Labels */}
          <div style={{ background: 'var(--surface-raised)', padding: 16, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', marginBottom: 16 }}>
            <h4 style={{ marginTop: 0 }}>Labels</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
              {w.labels.length === 0 && <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>No labels yet.</span>}
              {w.labels.map((l) => (
                <span key={l} className="tag-chip">
                  {l}
                  <button
                    onClick={() => patch.mutate({ labels: w.labels.filter((x) => x !== l) })}
                    aria-label={`Remove label ${l}`}
                    style={{ padding: '0 4px', fontSize: 11, lineHeight: 1, background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <input
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && labelInput.trim()) {
                  const next = [...new Set([...w.labels, labelInput.trim()])];
                  patch.mutate({ labels: next });
                  setLabelInput('');
                }
              }}
              placeholder="Type and Enter to add"
              style={{ width: '100%', padding: '4px 6px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 4 }}
            />
          </div>

          {/* 0.1.52 — CTSK-167 Issue A: workitem-level attachments panel.
              Reads `attachments[]` on the detail response (populated since
              0.1.46). Rendered ABOVE Linked documents so a workitem that's
              mostly attachment-driven (e.g. session-mint docs on CTSK-164)
              surfaces them first. Suppressed when there are zero. */}
          <AttachmentsPanel
            attachments={w.attachments ?? []}
            onNavigate={onNavigate}
          />

          {/* Linked documents */}
          <LinkedDocumentsPanel
            workItemId={id}
            links={(w.relatedDocuments ?? []) as unknown as Parameters<typeof LinkedDocumentsPanel>[0]['links']}
            onNavigate={onNavigate}
          />

          {/* CTSK-509 — post-it notes on this work item (hidden when enableNotes is off). */}
          <NotesPanel targetType="workitem" targetId={id} onNavigate={onNavigate} />

          {/* People — Lead, Assignees, Reviewers, Teams (groups) */}
          <div style={{ background: 'var(--surface-raised)', padding: 16, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', marginBottom: 16 }}>
            <h4 style={{ marginTop: 0 }}>People</h4>

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Lead</div>
              <UserSelect
                value={w.leadId ?? null}
                onChange={(next) => patch.mutate({ leadId: next })}
                placeholder="— No lead —"
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                Assignees{' '}
                {w.assigneeIds.length > 0 && (
                  <span style={{ marginLeft: 4 }}><UserChips ids={w.assigneeIds} /></span>
                )}
              </div>
              <UserMultiSelect
                selected={w.assigneeIds}
                onChange={(next) => patch.mutate({ assigneeIds: next })}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                Reviewers{' '}
                {w.reviewerIds.length > 0 && (
                  <span style={{ marginLeft: 4 }}><UserChips ids={w.reviewerIds} /></span>
                )}
              </div>
              <UserMultiSelect
                selected={w.reviewerIds}
                onChange={(next) => patch.mutate({ reviewerIds: next })}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Teams / Groups</div>
              <GroupMultiSelect
                selected={w.groupIds}
                onChange={(next) => patch.mutate({ groupIds: next })}
              />
            </div>
          </div>

          {/* Details */}
          <div style={{ background: 'var(--surface-raised)', padding: 16, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', marginBottom: 16 }}>
            <h4 style={{ marginTop: 0 }}>Details</h4>
            <dl className="kv">
              <dt>Stage</dt><dd style={{ textTransform: 'capitalize' }}>{w.workflowStage.replace('_', ' ')}</dd>
              <dt>Created</dt><dd>{new Date(w.createdAt).toLocaleString()}</dd>
              <dt>Updated</dt><dd>{new Date(w.updatedAt).toLocaleString()}</dd>
              <dt>Start date</dt><dd>{w.startDate ? new Date(w.startDate).toLocaleDateString() : <span style={{ color: 'var(--text-muted)' }}>—</span>}</dd>
            </dl>
          </div>

          {/* Workflow loading indicator (only shown when needed) */}
          {!workflow && workflowsQ.isLoading && (
            <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 16 }}>
              Loading workflow…
            </div>
          )}

          {/* Activity */}
          <div style={{ background: 'var(--surface-raised)', padding: 16, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
            <h4 style={{ marginTop: 0 }}>Activity</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 360, overflow: 'auto' }}>
              {(w.activityLog as ActivityEntry[]).length === 0 && (
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>No activity yet.</span>
              )}
              {(w.activityLog as ActivityEntry[]).slice(0, 50).map((a) => (
                <div key={a.id} style={{ fontSize: 12, paddingBottom: 6, borderBottom: '1px dotted var(--border-subtle)' }}>
                  {/* 0.1.52 — CTSK-168: actor identity line moved above
                      the timestamp so the audit trail reads as "who did
                      what when" instead of "when ??? did what". */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)', gap: 8 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
                      <ActorIdentity
                        displayName={a.actorDisplayName ?? null}
                        email={a.actorEmail ?? null}
                        id={a.actorId}
                      />
                      {a.source && <SourceTagChip tag={a.source} />}
                    </span>
                    <span>{new Date(a.timestamp).toLocaleString()}</span>
                  </div>
                  <div>
                    <strong style={{ textTransform: 'capitalize' }}>{a.type.replace('_', ' ')}</strong>
                    {a.message && <span style={{ color: 'var(--text-secondary)' }}> — {a.message}</span>}
                  </div>
                  {a.payload && Object.keys(a.payload).length > 0 && (
                    <code style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)' }}>
                      {JSON.stringify(a.payload)}
                    </code>
                  )}
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}

/**
 * 0.1.52 — CTSK-168: renders "who did this" for a comment or activity row.
 * Server hydrates authorDisplayName + email off the ULID in the detail
 * response. When present, we show the display name; when absent (deleted
 * user / stale ULID), we fall back to a shortened ULID (`01KWCZ…N6V1B2C`)
 * with the full ULID surfaced via `title=`. Email is tooltip-only.
 * Rendered inline (span) so it can slot into either a comment header
 * or an activity row without disturbing layout.
 */
function ActorIdentity({
  displayName,
  email,
  id,
}: {
  displayName: string | null;
  email: string | null;
  id: string;
}) {
  if (displayName) {
    // Full ULID + email in tooltip so the operator can still cross-
    // reference the raw identifier without cluttering the row.
    const tooltipParts: string[] = [id];
    if (email) tooltipParts.push(email);
    return (
      <span title={tooltipParts.join(' · ')} style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
        {displayName}
      </span>
    );
  }
  // Unknown / deleted user — shortened ULID with full form in tooltip.
  const short = id.length > 14 ? `${id.slice(0, 6)}…${id.slice(-6)}` : id;
  return (
    <span
      title={id}
      style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}
    >
      {short}
    </span>
  );
}

/**
 * 0.1.52 — CTSK-168: session-attribution tag chip. Shows which session
 * / agent authored this activity or comment (e.g. `cowork-coo-mini-me`
 * for a mini-me shell, `pdf-text-build` for a build session).
 * Populated from `source` (CTSK-154 Phase C). Rendered as a mono-spaced
 * mini-pill; monospace + muted background communicates "system tag" so
 * it's visually distinct from the display-name identity next to it.
 */
function SourceTagChip({ tag }: { tag: string }) {
  return (
    <span
      title={`Session source tag: ${tag}`}
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        background: 'var(--surface-sunken)',
        color: 'var(--text-secondary)',
        padding: '1px 5px',
        borderRadius: 3,
        border: '1px solid var(--border-subtle)',
        whiteSpace: 'nowrap',
      }}
    >
      {tag}
    </span>
  );
}

/**
 * State picker — renders the full workflow sequence in position order with the
 * current state highlighted IN PLACE. Available transitions are clickable;
 * unreachable states are dimmed so you can see direction (forward / backward)
 * relative to your current spot.
 */
function StatePicker({
  workflow,
  currentStatus,
  availableTransitions,
  onTransition,
  pending,
}: {
  workflow: WorkflowDefinition | undefined;
  currentStatus: string;
  availableTransitions: AvailableTransitionEntry[];
  onTransition: (to: string) => void;
  pending: boolean;
}) {
  // Fallback: workflow not yet loaded — show current state + transition buttons in legacy form.
  if (!workflow) {
    return (
      <>
        <div style={{ marginBottom: 8 }}><StatusBadge status={currentStatus} variant="document" /></div>
        {availableTransitions.length === 0
          ? <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>Terminal state — no further transitions.</p>
          : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {availableTransitions.map((t) => (
                <button
                  key={t.to}
                  className="btn"
                  style={{ fontSize: 12, padding: '4px 10px', opacity: t.allowed ? 1 : 0.5 }}
                  onClick={() => onTransition(t.to)}
                  disabled={pending || !t.allowed}
                  title={t.allowed ? undefined : t.message}
                >
                  → {t.to.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          )}
      </>
    );
  }

  const states = workflow.states.slice().sort((a, b) => a.position - b.position);
  const currentIdx = states.findIndex((s) => s.id === currentStatus);
  // CTSK-226 — availableTransitions is now {to, allowed, reason?, message?,
  // capability?}[] instead of string[]. Map by `to` for O(1) lookups; the
  // set of KEYS is exactly what the old `new Set(availableTransitions)`
  // held (every structurally-reachable target, guarded or not — non-goal
  // per the ticket is hiding guarded targets), but now each entry also
  // carries WHY a reachable-but-guarded target would 422 if clicked.
  const availableByTo = new Map(availableTransitions.map((t) => [t.to, t]));

  return (
    <>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
        Click any highlighted state to transition. The current state is shown in place; greyed states aren't reachable in one move.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {states.map((state, idx) => {
          const isCurrent = state.id === currentStatus;
          const entry = availableByTo.get(state.id);
          const isAvailable = !!entry;
          // CTSK-226 — reachable but currently guard-blocked (e.g. no
          // assignee, missing capability). Still rendered as a
          // structurally-available row (not lumped in with "unreachable"
          // below) so the operator can see direction + WHY, instead of
          // clicking through to a 422.
          const blocked = isAvailable && !entry!.allowed;
          const direction = idx < currentIdx ? 'back' : idx > currentIdx ? 'forward' : 'here';
          const clickable = isAvailable && !blocked && !pending;

          const arrow =
            direction === 'here' ? '◉'
            : direction === 'back' ? '↑'
            : '↓';

          // Visual states:
          //   current     → solid colored band with white text
          //   available   → white with state color stripe + chevron
          //   unreachable → very dim, no interactivity
          const baseStyle: React.CSSProperties = {
            display: 'grid',
            gridTemplateColumns: '20px 14px 1fr auto',
            alignItems: 'center',
            gap: 8,
            padding: '6px 10px',
            borderRadius: 4,
            fontSize: 13,
            border: 'none',
            textAlign: 'left',
            width: '100%',
            transition: 'background-color 120ms, opacity 120ms',
          };

          if (isCurrent) {
            return (
              <div
                key={state.id}
                style={{
                  ...baseStyle,
                  background: state.color,
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'default',
                }}
                aria-current="step"
              >
                <span style={{ fontSize: 14, lineHeight: 1 }}>{arrow}</span>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#fff', display: 'inline-block', boxShadow: '0 0 0 2px ' + state.color }} />
                <span>{state.label}</span>
                <span style={{ fontSize: 10, opacity: 0.8, textTransform: 'uppercase', letterSpacing: '.06em' }}>current</span>
              </div>
            );
          }

          if (isAvailable) {
            return (
              <button
                key={state.id}
                type="button"
                onClick={() => onTransition(state.id)}
                disabled={!clickable}
                style={{
                  ...baseStyle,
                  background: 'var(--surface-raised)',
                  borderLeft: `3px solid ${state.color}`,
                  cursor: clickable ? 'pointer' : blocked ? 'not-allowed' : 'wait',
                  color: 'var(--text-primary)',
                  opacity: blocked ? 0.55 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!blocked) e.currentTarget.style.background = 'var(--surface-sunken)';
                }}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--surface-raised)')}
                title={
                  blocked
                    ? (entry!.message ?? 'Not allowed right now')
                    : direction === 'back' ? 'Move backward to this state' : 'Move forward to this state'
                }
              >
                <span style={{ fontSize: 14, lineHeight: 1, color: 'var(--text-muted)' }}>{arrow}</span>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: state.color, display: 'inline-block' }} />
                <span>{state.label}</span>
                <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                  {blocked ? 'blocked' : direction === 'back' ? 'back' : 'forward'}
                </span>
              </button>
            );
          }

          // Unreachable — dimmed, no interaction
          return (
            <div
              key={state.id}
              style={{
                ...baseStyle,
                background: 'transparent',
                color: 'var(--text-muted)',
                opacity: 0.5,
                cursor: 'not-allowed',
              }}
              title="Not reachable in one transition from the current state"
            >
              <span style={{ fontSize: 14, lineHeight: 1 }}>{arrow}</span>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'transparent', border: `1.5px solid ${state.color}`, display: 'inline-block', opacity: 0.4 }} />
              <span>{state.label}</span>
              <span></span>
            </div>
          );
        })}
      </div>
    </>
  );
}
